import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Chip,
    Grid2,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Alert,
    Tabs,
    Tab,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    InputAdornment,
    IconButton
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Schedule as ScheduleIcon,
    CheckCircle as ExecutedIcon,
    HourglassEmpty as PendingIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Timeline as TimelineIcon,
    Visibility as ViewIcon,
    PlayArrow as ExecuteIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import TransactionStatus from '../common/TransactionStatus';
import { useWallet } from '../context/WagmiWalletContext';
import { useContracts } from '../context/ContractContext';
import { ethers } from 'ethers';
import { validateHexString } from '../../utils/validateHexString';
import logger from '../../utils/logger';
import TimelockOperations from './TimelockOperations-v2';

interface OperationDetails {
    hash: string;
    exists: boolean;
    isPending: boolean;
    isReady: boolean;
    isDone: boolean;
    state: string;
    executionTime: string;
    timeRemaining: string;
}

// Helper function to check a scheduled operation
async function checkScheduledOperation(timelockContract: ethers.Contract, args: { target: string; value: string; data: string; predecessor: string; salt: string }) {
    try {
        // Extract the parameters from the schedule call
        const { target, value, data, predecessor, salt } = args;

        // Compute the operation hash
        const operationHash = await timelockContract.hashOperation(
            target, value, data, predecessor, salt
        );
        // logger.log(`Operation hash: ${operationHash}`);

        // Check operation status
        const exists = await timelockContract.isOperation(operationHash);
        if (!exists) {
            logger.log("Operation does not exist");
            return null;
        }

        const isPending = await timelockContract.isOperationPending(operationHash);
        const isReady = await timelockContract.isOperationReady(operationHash);
        const isDone = await timelockContract.isOperationDone(operationHash);

        // Get the state as enum (more readable)
        const stateNumber = await timelockContract.getOperationState(operationHash);
        const states = ["Unset", "Waiting", "Ready", "Done"];

        // Get scheduled timestamp
        const timestamp = await timelockContract.getTimestamp(operationHash);
        const currentTime = Math.floor(Date.now() / 1000);
        const timeRemaining = timestamp > currentTime ?
            `${((timestamp - currentTime) / 60).toFixed(1)} minutes` :
            "Ready for execution";

        return {
            hash: operationHash,
            exists,
            isPending,
            isReady,
            isDone,
            state: states[stateNumber],
            executionTime: new Date(timestamp * 1000).toLocaleString(),
            timeRemaining
        };
    } catch (error) {
        logger.error("Error checking operation:", error);
        return null;
    }
}

// Helper function to get all scheduled operations
async function getAllScheduledOperations(timelockContract: ethers.Contract) {
    try {
        // Create filter for CallScheduled events
        const filter = timelockContract.filters.CallScheduled();
        // Query for events (you can add block ranges if needed)
        const scheduledEvents = await timelockContract.queryFilter(filter);

        // Process the events into a more usable format
        const operations = await Promise.all(scheduledEvents.map(async (event) => {
            const operationId = event.args?.id;
            const isPending = await timelockContract.isOperationPending(operationId);
            const isReady = await timelockContract.isOperationReady(operationId);
            const state = await timelockContract.getOperationState(operationId);
            const timestamp = await timelockContract.getTimestamp(operationId);
            const currentTime = Math.floor(Date.now() / 1000);

            // Only include operations that are still pending or ready
            if (isPending || isReady) {
                return {
                    id: operationId,
                    target: event.args?.target || '',
                    value: event.args?.value?.toString() || '',
                    isPending,
                    isReady,
                    state: ["Unset", "Waiting", "Ready", "Done"][state],
                    executionTime: new Date(timestamp * 1000).toLocaleString(),
                    timeRemaining: timestamp > currentTime ?
                        `${((timestamp - currentTime) / 60).toFixed(1)} minutes` :
                        "Ready for execution"
                };
            }
            return null;
        }));

        // Filter out null entries (completed operations)
        return operations.filter(op => op !== null);
    } catch (error) {
        logger.error("Error fetching scheduled operations:", error);
        return [];
    }
}

// Interface for tab panel props
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// Tab panel component
function TabPanel({ children, value, index, ...other }: TabPanelProps) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`timelock-tabpanel-${index}`}
            aria-labelledby={`timelock-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

// Main component
const TimelockManager: React.FC = () => {
    const { provider, signer, executionStatus, setExecutionStatus } = useWallet();
    const { contracts } = useContracts();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);

    // Handle tab changes
    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };


    // State variables
    const [operationParams, setOperationParams] = useState({
        target: "",
        value: "",
        data: "",
        predecessor: "0x0000000000000000000000000000000000000000000000000000000000000000",
        salt: "0x0000000000000000000000000000000000000000000000000000000000000000"
    });

    const [operationParamErrors, setOperationParamErrors] = useState({
        target: null,
        value: null,
        data: null,
        predecessor: null,
        salt: null
    });

    const [operationDetails, setOperationDetails] = useState<OperationDetails | null>(null);
    const [allOperations, setAllOperations] = useState<{ id: string; target: string; state: string; executionTime: string; timeRemaining: string }[]>([]);
    const [trackedOperations, setTrackedOperations] = useState<{
        id: string;
        target: string;
        status: 'scheduled' | 'executable' | 'executed';
        scheduledTx: string;
        executedTx?: string;
        executionTime: number;
    }[]>([]);
    const [transactionValue, setTransactionValue] = useState('');

    // Get operation statistics
    const getOperationStats = () => {
        const ready = allOperations.filter(op => op.state === 'Ready').length;
        const waiting = allOperations.filter(op => op.state === 'Waiting').length;
        const tracked = trackedOperations.length;

        return { total: allOperations.length, ready, waiting, tracked };
    };

    const stats = getOperationStats();

    // Load tracked operations on mount
    useEffect(() => {
        const savedOperations = localStorage.getItem('memer_tracked_operations');
        if (savedOperations) {
            try {
                setTrackedOperations(JSON.parse(savedOperations));
            } catch (error) {
                logger.error("Error parsing saved operations:", error);
            }
        }
    }, []);

    // Save tracked operations when they change
    useEffect(() => {
        if (trackedOperations.length > 0) {
            localStorage.setItem('memer_tracked_operations', JSON.stringify(trackedOperations));
        }
    }, [trackedOperations]);

    // Input validation
    const validateOperationParam = (field: string, value: string) => {
        switch (field) {
            case 'target':
                return ethers.utils.isAddress(value)
                    ? null
                    : "Invalid Ethereum address";

            case 'value':
                return /^[\d.]+$/.test(value)
                    ? null
                    : "Value must be a number";

            case 'data':
                return validateHexString(value, "Data").isValid
                    ? null
                    : validateHexString(value, "Data").message;

            case 'predecessor':
            case 'salt':
                return validateHexString(value, field).isValid
                    ? null
                    : validateHexString(value, field).message;

            default:
                return null;
        }
    };

    const handleOperationParamChange = (field: string, value: string) => {
        const error = validateOperationParam(field, value);

        setOperationParamErrors({
            ...operationParamErrors,
            [field]: error
        });

        setOperationParams({
            ...operationParams,
            [field]: value
        });
    };

    // Check operation
    const handleCheckOperation = async () => {
        if (!provider || !signer) {
            setExecutionStatus("Provider or Signer not initialized.");
            return;
        }

        try {
            // Find the timelock contract
            const timelockContract = contracts.find(c => c.name.toLowerCase().includes("timelock"));

            if (!timelockContract) {
                setExecutionStatus("Timelock contract not found in contracts list.");
                return;
            }

            // Create a contract instance
            const timelockInstance = new ethers.Contract(
                timelockContract.address,
                timelockContract.abi,
                signer
            );

            // Check the operation
            const details = await checkScheduledOperation(timelockInstance, operationParams);

            if (details !== null) {
                setOperationDetails(details);
                setExecutionStatus(`Operation ${details.exists ? "exists" : "does not exist"}`);
            } else {
                setOperationDetails(null);
                setExecutionStatus("Failed to check operation.");
            }
        } catch (error) {
            logger.error("Error:", error);
            setExecutionStatus("Error checking operation. See logger.");
        }
    };

    // Check timelock operation from transaction hash
    const checkTimelockOperationFromTx = async (txHash: string) => {
        if (!provider || !signer) return;

        try {
            // Get transaction receipt
            const receipt = await provider.getTransactionReceipt(txHash);
            if (!receipt) {
                setExecutionStatus("Transaction not found");
                return;
            }

            // Find the timelock contract
            const timelockContract = contracts.find(c => c.name.toLowerCase().includes("timelock"));
            if (!timelockContract) {
                setExecutionStatus("Timelock contract not found");
                return;
            }

            // Analyze logs for CallScheduled events
            const iface = new ethers.utils.Interface(timelockContract.abi);

            for (const log of receipt.logs) {
                if (log.address.toLowerCase() === timelockContract.address.toLowerCase()) {
                    try {
                        const parsedLog = iface.parseLog(log);
                        if (parsedLog && parsedLog.name === 'CallScheduled') {
                            const operationId = parsedLog.args.id;

                            // Now check the operation's status
                            const timelockInstance = new ethers.Contract(
                                timelockContract.address,
                                timelockContract.abi,
                                signer
                            );

                            const isOperation = await timelockInstance.isOperation(operationId);
                            const isPending = isOperation ? await timelockInstance.isOperationPending(operationId) : false;
                            const isReady = isOperation ? await timelockInstance.isOperationReady(operationId) : false;

                            setExecutionStatus(`Found operation ID: ${operationId}\nStatus: ${isOperation ? 'Exists' : 'Not found'}\nPending: ${isPending}\nReady: ${isReady}`);

                            // Add to operations list
                            const state = isReady ? "Ready" : isPending ? "Waiting" : "Unknown";
                            setAllOperations(prev => {
                                if (prev.some(op => op.id === operationId)) return prev;
                                return [...prev, {
                                    id: operationId,
                                    target: parsedLog.args.target,
                                    state,
                                    executionTime: "Unknown", // You could fetch this if needed
                                    timeRemaining: "Unknown"  // You could calculate this if needed
                                }];
                            });

                            return;
                        }
                    } catch (error) {
                        logger.error("Error parsing log:", error);
                    }
                }
            }

            setExecutionStatus("No timelock operation found in this transaction");
        } catch (error) {
            logger.error("Error checking for timelock operation:", error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            setExecutionStatus(`Error: ${errorMessage}`);
        }
    };

    // Fetch all operations
    const handleFetchAllOperations = async () => {
        if (!provider) {
            setExecutionStatus("Provider not initialized.");
            return;
        }

        try {
            // Find the timelock contract
            const timelockContract = contracts.find(c => c.name.toLowerCase().includes("timelock"));
            if (!timelockContract) {
                setExecutionStatus("Timelock contract not found.");
                return;
            }

            const timelockInstance = new ethers.Contract(
                timelockContract.address,
                timelockContract.abi,
                provider.getSigner()
            );

            const operations = await getAllScheduledOperations(timelockInstance);
            logger.log("FULL OPERATION DETAILS:", JSON.stringify(operations, null, 2));
            setAllOperations(operations);
            setExecutionStatus(`Found ${operations.length} active operations`);
        } catch (error) {
            logger.error("Error fetching operations:", error);
            setExecutionStatus("Error fetching operations. See logger.");
        }
    };

    // Execute a timelock operation
    const executeTimelockOperation = async (operationId: string) => {
        if (!provider || !signer) {
            setExecutionStatus("Provider or Signer not initialized.");
            return;
        }

        try {
            // Find the timelock contract
            const timelockContract = contracts.find(c => c.name.toLowerCase().includes("timelock"));
            if (!timelockContract) {
                setExecutionStatus("Timelock contract not found.");
                return;
            }

            const timelockInstance = new ethers.Contract(
                timelockContract.address,
                timelockContract.abi,
                signer
            );

            // Get the operation details
            const operation = allOperations.find(op => op.id === operationId);
            if (!operation) {
                setExecutionStatus("Operation not found.");
                return;
            }

            // Check if operation is ready
            const isReady = await timelockInstance.isOperationReady(operationId);
            if (!isReady) {
                setExecutionStatus("Operation is not ready for execution yet.");
                return;
            }

            // Execute the operation
            const txResponse = await timelockInstance.execute(
                operation.target,
                ethers.constants.Zero, // value - assuming 0 ETH
                "0x", // data - assuming empty data
                ethers.constants.HashZero, // predecessor
                ethers.utils.keccak256(ethers.utils.toUtf8Bytes("salt")) // salt
            );

            // Track the transaction (this function needs to be implemented or imported)
            // await trackTransactionChain(txResponse, setExecutionStatus, contracts);
            setExecutionStatus(`Transaction submitted: ${txResponse.hash}`);

            // Refresh the operations list
            handleFetchAllOperations();
        } catch (error) {
            logger.error("Error executing operation:", error);
            setExecutionStatus("Error executing operation. Check logger.");
        }
    };

    const traceTransactionChain = async (txHash: string) => {
        // This function would need to be implemented or imported
        setExecutionStatus(`Tracing transaction ${txHash}...`);
        // Implementation placeholder
    };

    // Render the component
    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Timelock Manager
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {loading && <CircularProgress size={24} />}
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleFetchAllOperations}
                        disabled={!provider || !signer || loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Summary Statistics */}
            <Grid2 container spacing={3} sx={{ mb: 3 }}>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <TimelineIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" fontWeight="bold">
                                {stats.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Operations
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <ExecutedIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" fontWeight="bold" color="success.main">
                                {stats.ready}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Ready to Execute
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <PendingIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" fontWeight="bold" color="warning.main">
                                {stats.waiting}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Waiting
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <ScheduleIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" fontWeight="bold" color="info.main">
                                {stats.tracked}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Tracked
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
            </Grid2>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab
                        icon={<SettingsIcon />}
                        label="Manual Operations"
                        iconPosition="start"
                    />
                    <Tab
                        icon={<TimelineIcon />}
                        label="Subgraph Data"
                        iconPosition="start"
                    />
                </Tabs>
            </Paper>

            <TabPanel value={activeTab} index={0}>
                <Grid2 container spacing={3}>
                    {/* Operation Finder Card */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <SearchIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    Find Operation
                                </Typography>
                            </Box>

                            <TextField
                                label="Transaction Hash"
                                fullWidth
                                placeholder="0x..."
                                value={transactionValue}
                                onChange={(e) => setTransactionValue(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={() => checkTimelockOperationFromTx(transactionValue)}
                                                disabled={!provider || !signer || !transactionValue}
                                            >
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ mb: 2 }}
                            />

                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<SearchIcon />}
                                onClick={() => checkTimelockOperationFromTx(transactionValue)}
                                disabled={!provider || !signer || !transactionValue}
                            >
                                Find Operation
                            </Button>
                        </Paper>
                    </Grid2>

                    {/* Manual Operation Check Card */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <SettingsIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    Manual Operation Check
                                </Typography>
                            </Box>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Operation Parameters</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid2 container spacing={2}>
                                        <Grid2 size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                label="Target Address"
                                                fullWidth
                                                size="small"
                                                value={operationParams.target}
                                                onChange={(e) => handleOperationParamChange('target', e.target.value)}
                                                error={!!operationParamErrors.target}
                                                helperText={operationParamErrors.target || "The address the timelock will call"}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                label="Value (ETH)"
                                                fullWidth
                                                size="small"
                                                value={operationParams.value}
                                                onChange={(e) => handleOperationParamChange('value', e.target.value)}
                                                error={!!operationParamErrors.value}
                                                helperText={operationParamErrors.value || "Amount of ETH to send (usually 0)"}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12 }}>
                                            <TextField
                                                label="Data"
                                                fullWidth
                                                size="small"
                                                value={operationParams.data}
                                                onChange={(e) => handleOperationParamChange('data', e.target.value)}
                                                error={!!operationParamErrors.data}
                                                helperText={operationParamErrors.data || "Encoded function call data (hex string)"}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                label="Predecessor"
                                                fullWidth
                                                size="small"
                                                value={operationParams.predecessor}
                                                onChange={(e) => handleOperationParamChange('predecessor', e.target.value)}
                                                error={!!operationParamErrors.predecessor}
                                                helperText={operationParamErrors.predecessor || "Operation ID that must be executed first"}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 6 }}>
                                            <TextField
                                                label="Salt"
                                                fullWidth
                                                size="small"
                                                value={operationParams.salt}
                                                onChange={(e) => handleOperationParamChange('salt', e.target.value)}
                                                error={!!operationParamErrors.salt}
                                                helperText={operationParamErrors.salt || "Unique identifier (hex string)"}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12 }}>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                onClick={handleCheckOperation}
                                                disabled={
                                                    !!operationParamErrors.target ||
                                                    !!operationParamErrors.value ||
                                                    !!operationParamErrors.data ||
                                                    !!operationParamErrors.predecessor ||
                                                    !!operationParamErrors.salt
                                                }
                                            >
                                                Check Operation
                                            </Button>
                                        </Grid2>
                                    </Grid2>
                                </AccordionDetails>
                            </Accordion>
                        </Paper>
                    </Grid2>

                    {/* Operation Details Card */}
                    {operationDetails && (
                        <Grid2 size={{ xs: 12 }}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Operation Details
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Grid2 container spacing={2}>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Operation Hash
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                            {operationDetails.hash}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Status
                                        </Typography>
                                        <Chip
                                            label={operationDetails.state}
                                            color={
                                                operationDetails.state === "Ready" ? "success" :
                                                    operationDetails.state === "Waiting" ? "warning" :
                                                        operationDetails.state === "Done" ? "default" : "error"
                                            }
                                            size="small"
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Execution Time
                                        </Typography>
                                        <Typography variant="body2">
                                            {operationDetails.executionTime}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Time Remaining
                                        </Typography>
                                        <Typography variant="body2">
                                            {operationDetails.timeRemaining}
                                        </Typography>
                                    </Grid2>
                                </Grid2>
                            </Paper>
                        </Grid2>
                    )}

                    {/* Tracked Operations */}
                    <Grid2 size={{ xs: 12 }}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Tracked Operations
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {trackedOperations.length > 0 ? (
                                <Grid2 container spacing={2}>
                                    {trackedOperations.map((op) => (
                                        <Grid2 size={{ xs: 12, md: 6, lg: 4 }} key={op.id}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                        <Chip
                                                            label={op.status}
                                                            color={
                                                                op.status === 'executed' ? 'success' :
                                                                    op.status === 'executable' ? 'warning' : 'default'
                                                            }
                                                            size="small"
                                                        />
                                                    </Box>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Operation ID
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                                                        {op.id.substring(0, 10)}...
                                                    </Typography>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Target
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                                                        {op.target.substring(0, 10)}...
                                                    </Typography>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Execution Time
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                                        {new Date(op.executionTime * 1000).toLocaleString()}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => traceTransactionChain(op.scheduledTx)}
                                                        >
                                                            Scheduled
                                                        </Button>
                                                        {op.executedTx && (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => traceTransactionChain(op.executedTx || '')}
                                                            >
                                                                Executed
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid2>
                                    ))}
                                </Grid2>
                            ) : (
                                <Alert severity="info">
                                    No operations tracked yet. Use the Transaction Builder to create operations.
                                </Alert>
                            )}
                        </Paper>
                    </Grid2>

                    {/* Active Operations */}
                    <Grid2 size={{ xs: 12 }}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Active Operations
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {allOperations.length > 0 ? (
                                <Grid2 container spacing={2}>
                                    {allOperations.map((op) => (
                                        <Grid2 size={{ xs: 12, md: 6, lg: 4 }} key={op.id}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                        <Chip
                                                            label={op.state}
                                                            color={op.state === "Ready" ? "success" : op.state === "Waiting" ? "warning" : "default"}
                                                            size="small"
                                                        />
                                                    </Box>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Operation ID
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                                                        {op.id.substring(0, 10)}...
                                                    </Typography>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Target
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                                                        {op.target.substring(0, 10)}...
                                                    </Typography>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Execution Time
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        {op.executionTime}
                                                    </Typography>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Time Remaining
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                                        {op.timeRemaining}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<ViewIcon />}
                                                        >
                                                            View
                                                        </Button>
                                                        {op.state === "Ready" && (
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                startIcon={<ExecuteIcon />}
                                                                onClick={() => executeTimelockOperation(op.id)}
                                                            >
                                                                Execute
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid2>
                                    ))}
                                </Grid2>
                            ) : (
                                <Alert severity="info">
                                    No active operations found. Try refreshing or finding an operation.
                                </Alert>
                            )}
                        </Paper>
                    </Grid2>
                </Grid2>

                {/* Status display */}
                {executionStatus && (
                    <Box sx={{ mt: 3 }}>
                        <TransactionStatus
                            status={executionStatus}
                            txHash={executionStatus.includes("Hash") ? executionStatus.split("Hash:")[1]?.trim() : undefined}
                            provider={provider || undefined}
                        />
                    </Box>
                )}
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                <TimelockOperations />
            </TabPanel>
        </Box>
    );
};

export default TimelockManager;
